import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { reportsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { fmtMoney } from '@/lib/format';

// The brand chart ramp lives in index.css as --chart-1..5 and is already wired
// into tailwind.config.js; these used to be hardcoded hex copies that drifted.
const COLORS = [
  'hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))',
  'hsl(var(--chart-4))', 'hsl(var(--chart-5))', 'hsl(var(--muted-foreground))',
];

export default function ReportsPage() {
  const [byStatus, setByStatus] = useState([]);
  const [byMonth, setByMonth] = useState([]);
  const [byCleaner, setByCleaner] = useState([]);

  useEffect(() => {
    // Failures are surfaced by the api response interceptor.
    reportsApi.get('jobs-by-status').then(r => setByStatus(r.rows)).catch(() => {});
    reportsApi.get('revenue-by-month').then(r => setByMonth(r.rows)).catch(() => {});
    reportsApi.get('jobs-by-cleaner').then(r => setByCleaner(r.rows)).catch(() => {});
  }, []);

  return (
    <AppShell title="Reports">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground">Operational and financial insights.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Jobs by status</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="count" nameKey="status" outerRadius={80}>
                    {byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Revenue by month</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonth}>
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="total" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Jobs by cleaner</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCleaner}>
                  {/* interval=0 + angle keeps every cleaner labelled; Recharts
                      silently drops horizontal ticks that do not fit. */}
                  <XAxis dataKey="cleaner_name" fontSize={11} interval={0} angle={-35} textAnchor="end" height={64} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
